import { useCallback, useState, type JSX } from 'react';
import { NZPVS_BOOKMARKLET, NZPVS_FILLER_VERSION } from './bookmarklet';

/** What the link says when it is clicked here instead of dragged. */
export const NZPVS_DRAG_HINT = 'Drag this link to your bookmarks bar.';

/** The permanent line under the link (Miranda M-S4). */
export const NZPVS_INSTALL_HOW =
  'On a computer, drag this link to your bookmarks bar. Clicking it here does nothing.';

/** The other way in, beside the "Copy bookmark address" button. */
export const NZPVS_INSTALL_BY_ADDRESS =
  'Or copy its address, add a bookmark, and paste that as its URL.';

/** The status line once the bookmark's address is on the clipboard — or could not be. */
export const NZPVS_ADDRESS_COPIED = 'Bookmark address copied.';
export const NZPVS_ADDRESS_NOT_COPIED = "Couldn't copy the address — drag the link instead.";

/**
 * The Settings drawer's "NZPVS filler" section (cca-uzt5): where the registrar
 * installs his CCAANZ → NZPVS bookmark — by dragging the link to his
 * bookmarks bar, or by copying its address into a bookmark he adds himself.
 * "NZPVS filler" is the section's title only; every instruction calls it
 * "your CCAANZ → NZPVS bookmark" (Miranda M-S5).
 *
 * The href is set through a ref, never as a JSX prop: React 19 replaces a
 * `javascript:` URL passed as `href` with one that throws (measured on
 * react-dom 19.2.8). The JSX carries a harmless in-page placeholder that the
 * ref overwrites on mount; React never writes the attribute again because the
 * prop never changes.
 *
 * The link must never RUN here: a click is prevented and answered with the
 * drag hint instead.
 */
export function NzpvsFillerInstall(): JSX.Element {
  const [hint, setHint] = useState('');
  const installHref = useCallback((el: HTMLAnchorElement | null) => {
    if (el) el.setAttribute('href', NZPVS_BOOKMARKLET);
  }, []);

  async function copyAddress() {
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    try {
      if (!clipboard || typeof clipboard.writeText !== 'function') throw new Error('no clipboard');
      await clipboard.writeText(NZPVS_BOOKMARKLET);
      setHint(NZPVS_ADDRESS_COPIED);
    } catch {
      setHint(NZPVS_ADDRESS_NOT_COPIED);
    }
  }

  return (
    <div style={{ fontSize: 13, lineHeight: 1.5 }}>
      <p style={{ margin: '0 0 8px' }}>
        Fills the NZ Police vetting request page from a lodgement row’s “Copy for NZPVS”: on the
        Police page, click your CCAANZ → NZPVS bookmark and paste. It attaches the Police consent
        form when the portal holds it, and ticks the identity checks declaration only when the
        parish certified the ID check. It never ticks the parent/guardian box and never submits —
        you check the highlighted fields and the attachment, tick that box if it applies, and submit
        yourself.
      </p>
      <p style={{ margin: '0 0 4px' }}>
        <a
          ref={installHref}
          href="#nzpvs-filler"
          draggable
          className="btn-link"
          style={{ fontWeight: 600 }}
          onClick={(e) => {
            e.preventDefault();
            setHint(NZPVS_DRAG_HINT);
          }}
        >
          CCAANZ → NZPVS
        </a>
      </p>
      <p style={{ margin: '0 0 8px', color: 'var(--color-muted)' }}>{NZPVS_INSTALL_HOW}</p>
      <p style={{ margin: '0 0 4px' }}>
        <button type="button" className="btn btn-link btn-sm" onClick={() => void copyAddress()}>
          Copy bookmark address
        </button>{' '}
        <span style={{ color: 'var(--color-muted)' }}>{NZPVS_INSTALL_BY_ADDRESS}</span>
      </p>
      <p role="status" style={{ margin: '0 0 4px', color: 'var(--color-muted)' }}>
        {hint}
      </p>
      <p style={{ margin: 0, color: 'var(--color-muted)' }}>
        Version {NZPVS_FILLER_VERSION}. If the Police page says your bookmark is out of date, drag
        this link again and delete the old one.
      </p>
    </div>
  );
}
